/**
 * MemberSheep's English copy. zh-TW.ts must match this shape (see MsCopy).
 * The privacy policy and terms are the governing versions; zh-TW.ts is a translation.
 * Multi-line headings use '\n' and render with white-space: pre-line.
 */
import { APPLE_STANDARD_EULA } from '@/membersheep/config';
import { LEGAL_NAME, SUPPORT_EMAIL } from '@/shared/site';

export interface MsLegalSection {
  id: string;
  heading: string;
  paragraphs: string[];
  items?: string[];
  links?: { label: string; href: string }[];
}

export interface MsLegalDoc {
  title: string;
  updated: string;
  summaryTitle?: string;
  summary?: string[];
  sections: MsLegalSection[];
}

const GOOGLE_PARTNER_SITES = 'https://policies.google.com/technologies/partner-sites';
const REVENUECAT_PRIVACY = 'https://www.revenuecat.com/privacy';
const APPLE_PRIVACY = 'https://www.apple.com/legal/privacy/';
const CLOUDFLARE_PRIVACY = 'https://www.cloudflare.com/privacypolicy/';
const GOOGLE_PRIVACY = 'https://policies.google.com/privacy';

export const en = {
  htmlLang: 'en',
  ogLocale: 'en_US',
  languageLabel: 'EN',

  nav: {
    home: 'MemberSheep home',
    primary: 'MemberSheep',
    privacy: 'Privacy',
    terms: 'Terms',
    support: 'Support',
    byLabs: 'by Travo Labs',
    language: 'Language'
  },

  seo: {
    /** 1200×630 share card in public/ (drawn by scripts/og-cards.html). */
    ogImage: '/og-image-membersheep.png',
    ogImageAlt: 'MemberSheep: every card, one flock. The membership card wallet for iPhone.',
    home: {
      title: 'MemberSheep: Membership Card Wallet for iPhone',
      description:
        'Keep every membership and loyalty card on your iPhone. Scan a barcode once, show it at the till in a tap. No account, data stays on your device.'
    },
    privacy: {
      title: 'Privacy Policy | MemberSheep',
      description: 'What MemberSheep keeps on your iPhone, what leaves it for ads and purchases, and the choices you have.'
    },
    terms: {
      title: 'Terms of Use | MemberSheep',
      description: 'The terms for using MemberSheep and MemberSheep Plus, built on Apple’s standard EULA.'
    },
    support: {
      title: 'Support | MemberSheep',
      description: 'Answers about hidden cards, restoring Plus, iCloud sync and ad privacy, and how to reach us.'
    },
    notFound: 'Page not found | MemberSheep'
  },

  home: {
    iconAlt: 'MemberSheep app icon',
    title: 'Every card.\nOne flock.',
    body: 'Scan your membership cards once and show them at the till in a tap. No account, no cloud of ours.',
    comingSoon: 'Coming soon to the App Store',
    download: 'Download on the App Store',
    /** Made-up member cards in the hero's stack (decoration, aria-hidden). */
    heroCards: {
      cafe: { name: 'Corner Café', kind: 'Stamp card' },
      gym: { name: 'Northside Gym', kind: 'Member' },
      grocer: { name: 'Green Grocer', kind: 'Rewards' },
      books: { name: 'Page & Co. Books', kind: 'Book club' }
    },
    features: [
      {
        title: 'Scan, import or type',
        body: 'Point the camera at a barcode, pick a photo of the card, or key in the number.'
      },
      {
        title: 'Bright at the till',
        body: 'Cards open at full brightness so the scanner reads them first time.'
      },
      {
        title: 'Stays on your iPhone',
        body: 'No sign-up. Sync with your own iCloud if you want it.'
      }
    ],
    plusLead: 'MemberSheep',
    plusAccent: 'Plus',
    plusBody:
      'Free for up to 5 cards and 2 categories, with ads. Plus lifts the limits and removes ads — monthly, yearly, or once for life.',
    plusItems: ['Unlimited cards and categories', 'No ads, anywhere', 'Home-screen widget for any card'],
    links: {
      privacy: 'What leaves your device, and why',
      terms: 'Subscriptions, trademarks, the fine print',
      support: `FAQ and ${SUPPORT_EMAIL}`
    }
  },

  quickAnswers: {
    title: 'Quick answers',
    items: [
      {
        question: 'Is MemberSheep free?',
        answer:
          'Yes. The free version holds up to 5 cards and 2 categories and shows ads. MemberSheep Plus removes the limits and the ads, as a monthly or yearly subscription or a one-time lifetime purchase.'
      },
      {
        question: 'Do I need an account?',
        answer:
          'No. There is no sign-up. Your cards stay on your iPhone, and you can sync them through your own iCloud if you want.'
      },
      {
        question: 'Which barcodes does it support?',
        answer:
          'QR, Aztec, Data Matrix, PDF417, Code 128, Code 39, Code 93, Codabar, EAN-13, EAN-8, UPC-A, UPC-E, ITF and ITF-14. Add a card by scanning it, importing a photo or typing the number.'
      },
      {
        question: 'Which iPhones does it run on?',
        answer: 'Any iPhone with iOS 17 or later. The app is in English and Traditional Chinese.'
      },
      {
        question: 'Who makes MemberSheep?',
        answer: 'Travo Labs, an independent studio in Taiwan that also makes the travel app Travo.'
      }
    ]
  },

  /** JSON-LD for the app entity (SoftwareApplication) on the home page. */
  schema: {
    description:
      'MemberSheep is an iPhone wallet for membership and loyalty cards. Scan a card’s barcode or QR code once and show it at the till in a tap, at full screen brightness. No account: cards stay on the device, with optional sync through your own iCloud. Free for 5 cards and 2 categories with ads; MemberSheep Plus (monthly, yearly or lifetime) removes the limits and the ads.',
    featureList: [
      'Add cards by scanning, importing a photo or typing the code',
      'Barcodes and QR codes in 14 formats, including EAN-13, Code 128, QR and PDF417',
      'Full screen brightness when a card opens',
      'Categories, search and most-recently-used order',
      'Home-screen widget for a chosen card',
      'Optional sync through your private iCloud',
      'No account; cards stay on your iPhone',
      'English and Traditional Chinese'
    ]
  },

  legal: {
    governs: 'This English version is the governing version.',
    onThisPage: 'On this page',
    otherLanguage: '繁體中文版'
  },

  privacy: {
    title: 'Privacy Policy',
    updated: 'Last updated 25 October 2026',
    summaryTitle: 'In short',
    summary: [
      'Your cards never reach us. They live on your iPhone, and in your own iCloud if you turn sync on.',
      'The free version shows Google AdMob ads, which use device and ad data as your consent choices allow.',
      'Purchases go through Apple. RevenueCat keeps an anonymous ID so the app knows you have Plus.'
    ],
    sections: [
      {
        id: 'who',
        heading: 'Who we are',
        paragraphs: [
          `MemberSheep is made by ${LEGAL_NAME}, an independent developer in Taiwan operating as Travo Labs (“we”, “us”). We are responsible for the app and this policy. Reach us at ${SUPPORT_EMAIL}.`,
          'MemberSheep has no accounts and no servers of our own that receive your cards. This policy explains what the app keeps, what the services it relies on receive, and what you can change.'
        ]
      },
      {
        id: 'device',
        heading: 'Data on your iPhone',
        paragraphs: [
          'The cards you add — names, barcode or QR values and formats, colours, logos, categories and notes — and when you last used each card are stored in the app’s storage on your iPhone, and in your own iCloud only if you turn on sync (below). The home-screen widget reads the same storage.',
          'Your iPhone’s own backups (iCloud Backup or a computer) include this storage, as they do for any app. We never receive it.'
        ]
      },
      {
        id: 'camera',
        heading: 'Camera and Photos',
        paragraphs: [
          'The camera is used only while you scan a card. Frames are read on your iPhone to find the code and are never saved or sent anywhere.',
          'When you import a card from Photos or pick a logo, the image you choose is read on your iPhone. Only the cropped logo you keep is saved, with the card.'
        ]
      },
      {
        id: 'icloud',
        heading: 'iCloud sync (optional)',
        paragraphs: [
          'iCloud sync is off until you turn it on in the app’s Settings. When it is on, your cards, categories and logos are stored in your private iCloud database under your Apple ID, so your devices stay in step. Apple stores this data; we cannot read your private database.',
          'You can turn sync off at any time. To remove the copy in iCloud, delete MemberSheep’s data from your iCloud storage in iOS Settings. Apple’s handling of iCloud data is covered by Apple’s privacy policy.'
        ],
        links: [{ label: 'Apple Privacy Policy', href: APPLE_PRIVACY }]
      },
      {
        id: 'ads',
        heading: 'Advertising (free version)',
        paragraphs: [
          'The free version shows ads from Google AdMob. To show and measure ads and prevent fraud, Google may receive your IP address, device and app information, ad interactions, and — only if you allow tracking — your device’s advertising identifier (IDFA).',
          'Before any ad loads, the app asks for the consent the law requires where you live: Google’s consent form in the European Economic Area, the United Kingdom and Switzerland, and Apple’s App Tracking Transparency prompt. You can change your ad choices later in the app’s Settings under Ad Privacy Choices (where offered), and tracking in iOS Settings → Privacy & Security → Tracking. If you decline, Google can still show ads using less data: in those regions it serves non-personalised or limited ads, and without tracking permission the advertising identifier is not shared.',
          'With MemberSheep Plus there are no ads, and the ads SDK is not started.'
        ],
        links: [{ label: 'How Google uses information from apps that use its services', href: GOOGLE_PARTNER_SITES }]
      },
      {
        id: 'purchases',
        heading: 'Purchases',
        paragraphs: [
          'MemberSheep Plus is bought through Apple. Apple processes the payment; we never see your payment details.',
          'The app uses RevenueCat to check whether Plus is active. RevenueCat receives an anonymous ID created by the app (not your name or Apple ID), your purchase history and receipts, and basic device information such as model, iOS version, App Store country and IP address. Your cards are never sent.'
        ],
        links: [{ label: 'RevenueCat Privacy Policy', href: REVENUECAT_PRIVACY }]
      },
      {
        id: 'not',
        heading: 'What we don’t do',
        paragraphs: [],
        items: [
          'No accounts, sign-ups or email collection in the app.',
          'No analytics or tracking SDKs of our own.',
          'We do not sell or share your personal information for money.'
        ]
      },
      {
        id: 'children',
        heading: 'Children',
        paragraphs: [
          `MemberSheep is not directed at children under 13, and we do not knowingly collect personal information from them. If you believe a child has given us personal information, contact ${SUPPORT_EMAIL} and we will delete it.`
        ]
      },
      {
        id: 'retention',
        heading: 'Keeping and deleting data',
        paragraphs: [
          'Cards stay on your iPhone until you delete them or the app, and in iCloud until you delete them there. Google and RevenueCat keep data under their own policies.',
          `To delete the purchase records RevenueCat holds for your app, email ${SUPPORT_EMAIL} and we will ask RevenueCat to delete them. Apple keeps its own record of your purchases.`
        ]
      },
      {
        id: 'rights',
        heading: 'Your rights',
        paragraphs: [
          'Depending on where you live — for example under the EU and UK GDPR, Taiwan’s Personal Data Protection Act, or California law — you may have the right to access, correct or delete your personal information, to object to or restrict its use, and to withdraw consent. Because your cards never reach us, most of this is in your hands through the controls above. For anything else, email us and we will reply within 30 days.',
          'You may also complain to your local data protection authority.'
        ]
      },
      {
        id: 'transfers',
        heading: 'International transfers',
        paragraphs: [
          'Google and RevenueCat may process data in the United States and other countries, under the safeguards described in their policies.'
        ]
      },
      {
        id: 'website',
        heading: 'This website',
        paragraphs: [
          'www.travolabs.com is hosted by Cloudflare, which processes IP addresses to deliver and protect the site. Visits are counted with Cloudflare Web Analytics, which sets no cookies. Fonts load from Google Fonts, so Google receives your IP address when a page loads.',
          'We set no cookies ourselves. If you play the demo video on Travo’s pages, it loads from YouTube, which may set cookies under Google’s policies.'
        ],
        links: [
          { label: 'Cloudflare Privacy Policy', href: CLOUDFLARE_PRIVACY },
          { label: 'Google Privacy Policy', href: GOOGLE_PRIVACY }
        ]
      },
      {
        id: 'changes',
        heading: 'Changes to this policy',
        paragraphs: [
          'If we change this policy, we will update the date above. If a change affects what the app collects, we will say so in the app or on this page before it applies.'
        ]
      },
      {
        id: 'contact',
        heading: 'Contact',
        paragraphs: [`Questions about privacy: ${SUPPORT_EMAIL}.`]
      }
    ]
  } satisfies MsLegalDoc as MsLegalDoc,

  terms: {
    title: 'Terms of Use',
    updated: 'Last updated 25 October 2026',
    sections: [
      {
        id: 'agreement',
        heading: 'Agreement',
        paragraphs: [
          `MemberSheep is licensed to you under Apple’s Licensed Application End User License Agreement (the “Standard EULA”). These terms add to it for MemberSheep, between you and ${LEGAL_NAME}, operating as Travo Labs (“we”). By using the app you agree to both. If the two conflict, the Standard EULA prevails.`
        ],
        links: [{ label: 'Apple’s Standard EULA', href: APPLE_STANDARD_EULA }]
      },
      {
        id: 'plus',
        heading: 'MemberSheep Plus',
        paragraphs: [
          'Plus is offered as a monthly or yearly auto-renewing subscription, or as a one-time lifetime purchase. Prices are shown in the app before you buy.'
        ],
        items: [
          'Payment is charged to your Apple ID when you confirm the purchase.',
          'A subscription renews automatically unless you cancel it at least 24 hours before the end of the current period. Your account is charged for renewal within the 24 hours before the period ends.',
          'Manage or cancel subscriptions in your Apple ID’s subscription settings, or from Manage Plus in the app.',
          'If a free trial is offered, any unused part of it ends when you buy a subscription.',
          'Refunds are handled by Apple under its policies. You can start a request from Manage Plus in the app.',
          'Restore Purchases brings Plus back on another device signed in to the same Apple ID.'
        ]
      },
      {
        id: 'free',
        heading: 'The free version and hidden cards',
        paragraphs: [
          'Without Plus, the app shows up to 5 cards and 2 categories, with ads. If Plus ends, cards and categories past those limits are hidden, not deleted, and come back when you resubscribe or restore your purchase.'
        ]
      },
      {
        id: 'cards',
        heading: 'Your cards',
        paragraphs: [
          'You are responsible for the cards you add. Only add cards you are entitled to use.',
          'Whether a shop accepts a code shown on a phone is up to the shop. We cannot promise that any merchant will scan or honour a card, and we are not responsible for benefits, points or discounts you miss.',
          'Your cards are stored on your device and, if you choose, in your iCloud. Keep backups; we cannot recover cards for you.'
        ]
      },
      {
        id: 'trademarks',
        heading: 'Brand names and logos',
        paragraphs: [
          'The names, logos and marks of the shops and brands offered as presets belong to their owners and are used only to help you recognise your own cards. MemberSheep is not affiliated with, endorsed by or sponsored by any of them.',
          'MemberSheep, Travo Labs and their logos are ours.'
        ]
      },
      {
        id: 'ads',
        heading: 'Ads',
        paragraphs: [
          'The free version shows ads from third parties. We do not control and are not responsible for advertisers or the sites and apps their ads lead to.'
        ]
      },
      {
        id: 'liability',
        heading: 'Disclaimer and liability',
        paragraphs: [
          'The app is provided “as is”, as set out in the Standard EULA. To the extent the law allows, we are not liable for indirect or consequential losses, or for losses from a card that does not scan or is not accepted. Nothing in these terms limits rights you have under consumer protection law that cannot be waived.'
        ]
      },
      {
        id: 'changes',
        heading: 'Changes',
        paragraphs: [
          'We may update the app and these terms. If we change these terms, we will update the date above; continuing to use the app after a change means you accept it.'
        ]
      },
      {
        id: 'law',
        heading: 'Governing law',
        paragraphs: [
          'These terms are governed by the laws of Taiwan (Republic of China). The Taiwan Taipei District Court has jurisdiction in the first instance, unless the consumer protection law of the country you live in gives you the right to bring a claim there.'
        ]
      },
      {
        id: 'contact',
        heading: 'Contact',
        paragraphs: [`Questions about these terms: ${SUPPORT_EMAIL}.`]
      }
    ]
  } satisfies MsLegalDoc as MsLegalDoc,

  support: {
    title: 'Support',
    faqTitle: 'Frequently asked questions',
    faq: [
      {
        question: 'Some of my cards disappeared after Plus ended.',
        answer:
          'They are hidden, not deleted. The free version shows your 5 oldest cards and first 2 categories. Resubscribe or restore your purchase and everything comes back, with each card’s category.'
      },
      {
        question: 'How do I restore Plus on a new iPhone?',
        answer:
          'Sign in with the same Apple ID, tap the Plus button on the home screen, then Restore Purchases.'
      },
      {
        question: 'How do I cancel my subscription or ask for a refund?',
        answer:
          'Open Manage Plus from the Plus button and choose Manage Subscription or Request a Refund. You can also cancel in your Apple ID’s subscription settings. Refunds are decided by Apple.'
      },
      {
        question: 'How does iCloud sync work?',
        answer:
          'Turn on iCloud Sync in the app’s Settings. Your cards go to your private iCloud database and appear on your other devices with the same Apple ID. We never see them. Turning sync off or signing out keeps the cards on your iPhone.'
      },
      {
        question: 'How do I move my cards to a new iPhone?',
        answer: 'Turn on iCloud Sync before you switch, or restore your new iPhone from a backup of the old one.'
      },
      {
        question: 'The shop’s scanner can’t read my card.',
        answer:
          'Open the card in the app rather than the widget: the app turns the screen to full brightness. Some code types can’t be shown yet (MaxiCode, GS1 DataBar, Micro QR). You can add the number with Type Code and pick a format the shop’s scanner reads (Code 128 by default), but some scanners only accept the card’s original type.'
      },
      {
        question: 'How do I change my ad privacy choices?',
        answer:
          'In the app’s Settings, tap Ad Privacy Choices (shown where the law requires it). Tracking permission is in iOS Settings → Privacy & Security → Tracking.'
      },
      {
        question: 'How do I delete all my data?',
        answer:
          'Delete the app to remove everything on your iPhone. If you used iCloud Sync, also delete MemberSheep’s data from your iCloud storage in iOS Settings.'
      }
    ],
    mailTitle: 'Still stuck?',
    mailBody: 'Email us and we will reply within two working days.'
  },

  notFound: {
    heading: 'This card isn’t in the wallet.',
    body: 'The page you’re looking for may have moved.',
    cta: 'Back to MemberSheep'
  },

  footer: {
    rights: 'Travo Labs'
  }
};

export type MsCopy = typeof en;
